import React, { useState, useImperativeHandle, forwardRef } from 'react';
import axios from 'axios';
import { API_POSTS } from '../../../utils';
import RenderBestTimeOffer from './RenderBestTimeOffer';
import ImageUploader from './ImageUploader';

import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog';

const OfferModal = forwardRef(({ onClose, onBack, props }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [offerTitle, setOfferTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [offerDetails, setOfferDetails] = useState('');
  const [voucherCode, setVoucherCode] = useState('');
  const [redeemLink, setRedeemLink] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [imageData, setImageData] = useState({
    originalURL: '',
    text: '',
    url: ''
  });
  const [scheduleDate, setScheduleDate] = useState(null);
  const [postSentSuccessModalOpen, setPostSentSuccessModalOpen] =
    useState(false);
  const [postErrorModalOpen, setPostErrorModalOpen] = useState(false);

  const handleClose = () => {
    onClose();
  };

  const handleScheduleChange = (selectedDate) => {
    if (selectedDate instanceof Date && !isNaN(selectedDate)) {
      setScheduleDate(Math.floor(selectedDate.getTime() / 1000));
    } else {
      setScheduleDate(null);
    }
  };

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  // Expose handlePost function to parent via ref
  useImperativeHandle(ref, () => ({
    handlePost
  }));

  const handlePost = async () => {
    const parseDate = (dateString) => {
      if (!dateString) return {};
      const date = new Date(dateString);
      return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
      };
    };

    const postBody = {
      clientId: props.currentClientId,
      message: offerTitle,
      images: [
        {
          originalURL: imageData.originalURL || '',
          text: imageData.text || '',
          url: imageData.url || ''
        }
      ],
      schedule: scheduleDate,
      createdAt: Math.floor(Date.now() / 1000),
      socialNetworks: {
        facebook: false,
        twitter: false,
        googleMyBusiness: true,
        instagram: false
      },
      isDraft: false,
      recurrentDates: [],
      posted: false,
      isScheduled: false,
      createdBy: props.currentCustomerName,
      hashtagsig: '',
      isPromotional: false,
      isOffer: true,
      offer: {
        title: offerTitle,
        schedule: {
          startDate: parseDate(startDate),
          endDate: parseDate(endDate)
        },
        summary: offerDetails,
        offer: {
          couponCode: voucherCode,
          redeemOnlineUrl: redeemLink,
          termsConditions: additionalNotes
        }
      }
    };

    // 🔹 Solo agregamos callToAction si hay un URL válido
    if (redeemLink.trim() !== '') {
      postBody.offer.callToAction = {
        actionType: 'ORDER',
        url: redeemLink
      };
    }

    try {
      const response = await axios.post(API_POSTS, JSON.stringify(postBody), {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.status === 200 || response.status === 201) {
        setPostSentSuccessModalOpen(true);
      }
    } catch (error) {
      setPostErrorModalOpen(true);
      console.error(error);
    }
  };

  return (
    <>
      {/* Image Upload Section */}
      <ImageUploader setImageData={setImageData} />

      <div className="space-y-4 mt-6">
        {/* Offer Title */}
        <div className="space-y-2">
          <Label htmlFor="offerTitle" className="text-base font-medium">
            Offer Title
          </Label>
          <Input
            type="text"
            id="offerTitle"
            onChange={(e) => setOfferTitle(e.target.value)}
            placeholder="Example: 20% off in store or online"
            className="h-12 text-base"
          />
        </div>

        {/* Start Date */}
        <div className="space-y-2">
          <Label htmlFor="startDate" className="text-base font-medium">
            Start Date
          </Label>
          <Input
            type="date"
            id="startDate"
            onChange={(e) => setStartDate(e.target.value)}
            className="h-12 text-base"
          />
        </div>

        {/* End Date */}
        <div className="space-y-2">
          <Label htmlFor="endDate" className="text-base font-medium">
            End Date
          </Label>
          <Input
            type="date"
            id="endDate"
            onChange={(e) => setEndDate(e.target.value)}
            className="h-12 text-base"
          />
        </div>
      </div>

      <RenderBestTimeOffer
        onChange={handleScheduleChange}
        currentClientTimeZone={props.currentClientTimeZone}
        currentClientBestTimes={props.currentClientBestTimes}
      />

      <div>
        <button
          onClick={toggleDropdown}
          className="btn w-100 text-start p-2 flex justify-between items-center"
          aria-expanded={isOpen}
          style={{
            marginTop: '15px',
            backgroundColor: 'transparent',
            color: 'inherit',
            border: 'none'
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = '#e0e0e0')
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = 'transparent')
          }>
          Add more details (optional)
          <span
            className={`ml-2 transform transition ${
              isOpen ? 'rotate-180' : ''
            }`}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              focusable="false"
              className="fill-current text-gray-600">
              <path d="M5.41 7.59L4 9l8 8 8-8-1.41-1.41L12 14.17z"></path>
            </svg>
          </span>
        </button>

        {isOpen && (
          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="offerDetails" className="text-base font-medium">
                Offer details
              </Label>
              <Textarea
                id="offerDetails"
                onChange={(e) => setOfferDetails(e.target.value)}
                rows="3"
                maxLength="1500"
                className="resize-none text-base"
                placeholder="Enter offer details (optional)"
              />
              <p className="text-xs text-muted-foreground">
                {offerDetails.length}/1500 characters entered
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="voucherCode" className="text-base font-medium">
                Voucher code (optional)
              </Label>
              <Input
                type="text"
                id="voucherCode"
                onChange={(e) => setVoucherCode(e.target.value)}
                className="h-12 text-base"
                placeholder="Enter voucher code"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="redeemLink" className="text-base font-medium">
                Link to redeem offer (optional)
              </Label>
              <Input
                type="url"
                id="redeemLink"
                onChange={(e) => setRedeemLink(e.target.value)}
                className="h-12 text-base"
                placeholder="Enter URL"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="additionalNotes"
                className="text-base font-medium">
                Additional notes (optional)
              </Label>
              <Textarea
                id="additionalNotes"
                onChange={(e) => setAdditionalNotes(e.target.value)}
                rows="3"
                className="resize-none text-base"
                placeholder="Enter any additional notes"
              />
            </div>
          </div>
        )}
      </div>

      {/* Success Modal */}
      <Dialog
        open={postSentSuccessModalOpen}
        onOpenChange={(open) => {
          setPostSentSuccessModalOpen(open);
          if (!open) {
            handleClose();
            window.location.reload();
          }
        }}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Success!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Your post has been sent
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => {
                setPostSentSuccessModalOpen(false);
                handleClose();
                window.location.reload();
              }}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <Dialog open={postErrorModalOpen} onOpenChange={setPostErrorModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Error!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              We have trouble adding the publication
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setPostErrorModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default OfferModal;
